import circle from '@turf/circle'
import _ from 'lodash'
import React, {useEffect, useMemo, useState} from 'react'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import {Button, Icon, IconGroup, Modal} from 'semantic-ui-react'
import { MatchProps } from '../../common/slice'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { getCoordinatesFromPath, replaceXWithSpace } from '../../common/util'
import {AP_POINT_RADIUS_MAX, AP_POINT_RADIUS_MIN, RECAPTCHA_ENABLED} from '../../environment'
import VctRecaptcha from '../vct-recaptcha/VctRecaptcha'
import {selectParcel, selectParcelByFilmAndFrame, selectParcelByPolygon} from './ap.slice'
import { APUtils } from './ap.util'
import APCart from './APCart'
import { selectCartItems } from './apCart.slice'

import APDataTable from './APDataTable'
import './APMainComponent.css'

import SearchBar from './SearchBar'
import {APDrawPolygonButton} from "./APDrawPolygonButton";
import {useLocation} from "react-router-dom";

export function APMainComponent({ match }: MatchProps) {
  const dispatch = useAppDispatch()
  const apData = APUtils.useAPData()
  const laisKey = match?.params?.laisKey
  const coordinates = match?.params?.coordinates
  const location = useLocation()
  const [showCart, setShowCart] = useState(false)
  const [shouldShowSplashDialog, setShouldShowSplashDialog] = useState (true)


  // This useEffect is OK - it will only trigger if a search hasn't been performed
  useEffect(() => {
    if (_.isEmpty(apData)) {
      if (laisKey) {
        dispatch(selectParcel({ LAISKEY: replaceXWithSpace(laisKey) }))
      } else if (coordinates) {
        const coordinatesFromPath = getCoordinatesFromPath(coordinates)
        if (coordinatesFromPath) {
          let radius = coordinatesFromPath.radius;
          // @ts-ignore the field is defined in the config file with a default value
          if (radius < AP_POINT_RADIUS_MIN) { radius = AP_POINT_RADIUS_MIN }
          // @ts-ignore the field is defined in the config file with a default value
          if (radius > AP_POINT_RADIUS_MAX) { radius = AP_POINT_RADIUS_MAX }

          const polygon = circle([coordinatesFromPath.longitude, coordinatesFromPath.latitude], radius, { units: 'meters' })
          dispatch(selectParcelByPolygon({ geometry: polygon.geometry, projection: 'EPSG:4326' }))
        }
      } else {
        // This is the part of the code where we look at just the query params - so for a URL
        // such as .../aerialphotography/?frame=XXX&film=YYY - we'll jump into processing those params to
        // update the initial view of the map
        let params = new URLSearchParams(location.search)
        if (params.has("frame") && params.has("film")) {
          let filmNo = params.get("film") || undefined
          let frameNo = params.get("frame") || undefined

          dispatch(selectParcelByFilmAndFrame({filmNo: filmNo, frameNo: frameNo}))
        }
      }
    }
  }, [laisKey, coordinates, apData, dispatch])

  /**
   * Returns a key to identify this component. This is used by the react CSSTransition to compare keys when re-rendering the component.
   * @returns {string|*}
   */
  const apDataKey = useMemo(() => {
    if (_.isEmpty(apData) || !apData) {
      return 'empty'
    } else {
      return apData.laisKey
    }
  }, [apData])

  const showDataTable = useMemo(() => {
    return apData && !_.isEmpty(apData) && !showCart
  }, [apData, showCart])

  const showSplashDialog = () => {
    return ( (!laisKey && !coordinates) && shouldShowSplashDialog);
  }

  return (
    <>
      <SearchBar/>
      <TransitionGroup>
        <CSSTransition
          key={apDataKey}
          appear={true}
          in={true}
          timeout={1000}>
          <>
            <div className={'apDetailsContainer'} style={{ display: 'flex', flexDirection: 'column' }}>
              {showDataTable && <APDataTable/>}
              {showCart && <APCart setShowCart={setShowCart}/>}
            </div>
            <CartButton showCart={showCart} setShowCart={setShowCart}/>
            <APDrawPolygonButton />
          </>
        </CSSTransition>
      </TransitionGroup>
      {
        RECAPTCHA_ENABLED && <VctRecaptcha/>
      }
      <Modal open={showSplashDialog()} style={{paddingBottom: "1em"}}>
        <Modal.Content>
          <p style={{fontWeight: 'bold'}}>
            Welcome
          </p>
          <p>
            The Aerial Photography Search Portal contains photographs from the early 1940’s through to the present
            day that have been captured or acquired by the Northern Territory Government. These photographs are an
            important record of development and landscape change over time. They also capture some key events and
            timeframes such as WWII and natural disasters (e.g. cyclones, floods, etc.). As a result, this index is
            an important resource for government, industry and the public.
          </p>
          <p>
            Searching is very easy with address, placename and coordinate searches available.
          </p>
          <ul style={{listStyleType: 'none'}}>
            <li>
              <p>
                <span style={{fontWeight: 'bold'}}>Simple Search</span> -
                Type in an address or name of a place/location into the search bar and click on the required item from
                the search list that is displayed as you type.
              </p>
            </li>
            <li>
              <p><span style={{fontWeight: 'bold'}}>Advanced Search</span> -
                Click on the "Advanced Search" button and select from either "Coordinate" or "Placename" and
                enter the relevant details.
              </p>
            </li>
            <li>
              <p><span style={{fontWeight: 'bold'}}>Draw Search Extents</span> -
                Click the "pencil" icon In the top right of the map and then click in the map to draw a polygon to
                search.
                Clicking over the first point (i.e. closing the polygon), initiates the search.
              </p>
            </li>
          </ul>
          <p> All of the photograph frames that cover the search area are displayed in the main map and listed on
            the left-hand-side. The list and display can be filtered by the year of capture to help refine the search.
            Clicking on a photograph frame in the list will zoom and highlight that frame on the map.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button floated={"left"} primary onClick={() => setShouldShowSplashDialog(false)}>Proceed</Button>
        </Modal.Actions>

      </Modal>
    </>
  )

}

type CartButtonProps = {
  showCart: boolean,
  setShowCart: (showCart: boolean) => void
}
const CartButton = ({ showCart, setShowCart }: CartButtonProps) => {
  const cartFrames = useAppSelector(selectCartItems)

  const handleCartClick = () => {
    setShowCart(!showCart)
  }

  return (
    <div className={'apCartButton'}>
      <IconGroup>
        <Icon link name={'cart'} color={showCart ? 'blue' : 'grey'} size={'huge'} onClick={handleCartClick}>
          <div style={{ position: 'relative', left: '6px', top: '-50px' }}>
            <h2 style={{
              textAlign: 'center',
              color: 'black',
              fontWeight: 'bolder',
              WebkitTextStroke: '1px white',
            }}>{cartFrames.length}</h2>
          </div>
        </Icon>
      </IconGroup>
    </div>
  )
}
